import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { RepositoryBinding } from '../models/repository-binding.model';
import { RepositoryBindingService } from './repository-binding.service';

export const WORKFLOWS_FOUND_KEY = 'projectForm.repositoryActions.workflowsFound';
export const ACTIONS_TOKEN_MISSING_KEY = 'projectForm.repositoryActions.tokenMissing';
export const ACTIONS_UNAVAILABLE_KEY = 'projectForm.repositoryActions.unavailable';

/** Was nach dem Speichern von einer Repository-URL zu berichten ist (NEX-1292). */
export interface ActionsHint {
  key: string;
  params?: Record<string, string | number>;
}

/** Ein {@link ActionsHint} mit der Repository-URL, zu der er gehoert. */
export interface RepositoryActions {
  repoUrl: string;
  hint: ActionsHint;
}

/**
 * Was hinter einer hinterlegten Repository-URL steckt: wie viele Workflows dort liegen und
 * ob ueberhaupt ein Token da ist, mit dem sich nachsehen laesst (NEX-1292).
 *
 * <p>Ohne diese Rueckmeldung bleibt beim Speichern offen, ob die Bindung je einen Lauf
 * zeigen wird — der Beobachter laeuft danach im Hintergrund und meldet sich von selbst
 * nicht.
 *
 * <p>Als Dienst und nicht im Formular: die Abbildung vom Fehlercode des Anbieters auf die
 * Meldung ist Fachlogik und soll ohne aufgebaute Oberflaeche pruefbar bleiben.
 */
@Injectable({ providedIn: 'root' })
export class RepositoryActionsService {
  private readonly bindings = inject(RepositoryBindingService);

  /**
   * Ein Bericht je angebundenem Repository des Projekts. Ein Fehler kostet nur seinen
   * eigenen Bericht: die uebrigen Repositories haben mit ihm nichts zu tun, und laesst
   * sich die Liste gar nicht laden, gibt es eben nichts zu berichten.
   */
  probe(projectId: string): Observable<RepositoryActions[]> {
    return this.bindings.list(projectId).pipe(
      switchMap((bindings) => {
        const probes = bindings.map((binding) => this.probeBinding(projectId, binding));
        return probes.length ? forkJoin(probes) : of([]);
      }),
      catchError(() => of([] as RepositoryActions[])),
    );
  }

  private probeBinding(projectId: string, binding: RepositoryBinding): Observable<RepositoryActions> {
    return this.bindings.pipelines(projectId, binding.id).pipe(
      map((listing) => ({
        repoUrl: binding.repoUrl,
        hint: { key: WORKFLOWS_FOUND_KEY, params: { count: listing.pipelines.length } },
      })),
      catchError((error: HttpErrorResponse) => of({ repoUrl: binding.repoUrl, hint: { key: hintKeyOf(error) } })),
    );
  }
}

/**
 * Die eine Rueckmeldung, die eine Meldung wert ist: ein Problem schlaegt den Erfolg, weil
 * es etwas zu tun gibt. Die uebrigen stehen ohnehin je Zeile im Formular.
 */
export function leadingHint(actions: RepositoryActions[]): ActionsHint | null {
  const problem = actions.find((action) => action.hint.key !== WORKFLOWS_FOUND_KEY);
  return (problem ?? actions[0])?.hint ?? null;
}

export function isTokenMissingHint(hint: ActionsHint): boolean {
  return hint.key === ACTIONS_TOKEN_MISSING_KEY;
}

/**
 * Das fehlende Token ist der einzige Fall, den die Person selbst beheben kann — jeder
 * andere Fehler des Anbieters bleibt fuer sie dasselbe: die Workflows sind nicht lesbar.
 */
function hintKeyOf(error: HttpErrorResponse): string {
  const body = error.error as { code?: string } | null;
  return (body?.code ?? '').endsWith('_TOKEN_MISSING') ? ACTIONS_TOKEN_MISSING_KEY : ACTIONS_UNAVAILABLE_KEY;
}
